import { ref, computed } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { aiConfigClient } from '~/services';

const defaultData = () => ({ company_name: '', company_pitch: '', settings: { role: '', tone: '' } });

export const useAiConfiguration = createGlobalState(() => {
    const toasted = useToasted();

    const teamId = Spark.state.currentTeam.id;

    const data = ref(defaultData());
    const errors = ref({});
    const success = ref(false);
    const submitting = ref(false);
    const loading = ref(true);

    let request = null;

    const mount = async () => {
        if (request) {
            return request;
        }

        errors.value = {};
        success.value = false;
        submitting.value = false;
        loading.value = true;

        request = (async () => {
            try {
                const response = (await aiConfigClient.getConfig(teamId)).data;
                data.value = {
                    id: response.id,
                    company_name: response.company_name,
                    company_pitch: response.company_pitch,
                    settings: {
                        role: response.settings?.role || '',
                        tone: response.settings?.tone || '',
                    },
                };
            } catch {
                data.value = defaultData();
            } finally {
                loading.value = false;
                request = null;
            }
        })();

        return request;
    };

    mount();

    const submit = async () => {
        if (submitting.value) {
            return;
        }

        errors.value = {};
        submitting.value = true;

        try {
            const payload = {
                ...data.value,
            };

            if (data.value.id) {
                const response = (await aiConfigClient.updateConfig(teamId, payload)).data;
                success.value = true;

                return response;
            } else {
                const response = (await aiConfigClient.createConfig(teamId, payload)).data;
                data.value = response;
                success.value = true;

                return response;
            }
        } catch (error) {
            success.value = false;
            if (error?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
                const responseErrors = error.response.data.errors;

                Object.keys(responseErrors).map((k) => {
                    errors.value[k] = responseErrors[k][0];
                });
            } else {
                toasted.global.platform_error();
            }

            throw error;
        } finally {
            submitting.value = false;
        }
    };

    const configured = computed(() => {
        return Boolean(data.value?.id && data.value?.settings?.role && data.value?.settings?.tone);
    });

    return { errors, submitting, loading, submit, mount, data, success, configured };
});
